import { z } from "zod";
import { anyCountry, countryNames } from "~/mydata";
import { timestamp } from "~/schemas/timestamp";
import {
  checkGreekVATNumber,
  isVATCountryEU,
  vatCountriesEU,
} from "~/vatnumbers";

export const client = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  vatNumber: z.string().min(1, "Υποχρεωτικό πεδίο"),
  country: z.enum(anyCountry),
  branch: z.number().int().nonnegative().optional().default(0),
  clientName: z.string().min(1, "Η επωνυμία είναι υποχρεωική"),
  activity: z.string().optional(),
  doy: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  addressStreet: z.string().optional(),
  addressNumber: z.number().int().nonnegative().optional(),
  email: z.string().optional(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  createdAt: timestamp,
  updatedAt: timestamp,
});

export const registerClientInput = client
  .pick({
    country: true,
    vatNumber: true,
    branch: true,
    clientName: true,
    city: true,
    number: true,
    addressStreet: true,
    addressNumber: true,
    postalCode: true,
    category: true,
    doy: true,
    email: true,
    phone1: true,
    phone2: true,
    activity: true,
  })
  .refine(
    // Check vat number for each country
    (client) => {
      if (client.country === "GR") {
        return checkGreekVATNumber(client.vatNumber);
      } else if (isVATCountryEU(client.country)) {
        const { regex } = vatCountriesEU[client.country];
        return (
          typeof client.vatNumber === "string" && regex.test(client.vatNumber)
        );
      } else {
        return true;
      }
    },
    (val) => {
      const path = ["vatNumber"];
      if (val.country === "GR") {
        return { path, message: "Μή έγκυρος Α.Φ.Μ" };
      } else if (isVATCountryEU(val.country)) {
        const { examples } = vatCountriesEU[val.country];
        return {
          path,
          message: `Μη έγκυρος αριθμός φορολόγησης για ${val.country} ${
            countryNames[val.country]
          } (π.χ. ${examples.join(", ")})`,
        };
      } else {
        return {
          path,
          message: `Μη έγκυρος αριθμός φορολόγησης για ${val.country} ${
            countryNames[val.country]
          }`,
        };
      }
    }
  );

export const registerClientOutput = z.object({
  created: z.boolean(),
  client: client,
});

export const editClientInput = client
  .pick({
    id: true,
    country: true,
    vatNumber: true,
    branch: true,
    clientName: true,
    city: true,
    number: true,
    addressStreet: true,
    addressNumber: true,
    postalCode: true,
    category: true,
    doy: true,
    email: true,
    phone1: true,
    phone2: true,
    activity: true,
  })
  .refine(
    // Check vat number for each country
    (client) => {
      if (client.country === "GR") {
        return checkGreekVATNumber(client.vatNumber);
      } else if (isVATCountryEU(client.country)) {
        const { regex } = vatCountriesEU[client.country];
        return (
          typeof client.vatNumber === "string" && regex.test(client.vatNumber)
        );
      } else {
        return true;
      }
    },
    (val) => {
      const path = ["vatNumber"];
      if (val.country === "GR") {
        return { path, message: "Μή έγκυρος Α.Φ.Μ" };
      } else if (isVATCountryEU(val.country)) {
        const { examples } = vatCountriesEU[val.country];
        return {
          path,
          message: `Μη έγκυρος αριθμός φορολόγησης για ${val.country} ${
            countryNames[val.country]
          } (π.χ. ${examples.join(", ")})`,
        };
      } else {
        return {
          path,
          message: `Μη έγκυρος αριθμός φορολόγησης για ${val.country} ${
            countryNames[val.country]
          }`,
        };
      }
    }
  );

export const editClientOutput = client;

export type Client = z.infer<typeof client>;
